<template>
  <div id="app" :class="{ 'tour-padding': isViewingTour }">
    <router-view/>
    <TheLoader v-if="isLoading" />
  </div>
</template>
<script>

import store from '@/store'
import { mapGetters, mapActions } from 'vuex'
import { EventBus } from '@/utils/eventBus'
import { Translation } from '@/utils/Translation'
import { AUTH_COOKIE_NAME, HIDE_CUBA_RECHARGE_BANNER } from '@/utils/constants'
import TheLoader from '@/components/TheLoader'
import { isNull, objectHasKeys } from '@/utils/comparators'
export default {
  name: 'App',
  components: {
    TheLoader
  },
  created () {
    this.enableLoading()
    this.whenUserIsUnauthorized()
    this.whenUserClicksLogout()
    this.whenTourIsVisibleAddSomePadding()
    this.whenUserBalanceChanges()
  },
  mounted () {
    this.acknowledgementDefaults()
  },
  methods: {
    ...mapActions({
      enableLoading: 'app/enableLoading'
    }),
    acknowledgementDefaults () {
      this.$toastr.defaultPosition = 'toast-bottom-center'
      this.$toastr.defaultTimeout = 5000
      this.$toastr.defaultProgressBar = false
      this.$toastr.defaultPreventDuplicates = true
      this.$toastr.defaultClassNames = ['tw-container']
      this.$toastr.defaultStyle = {
        width: '100%'
      }
    },
    whenTourIsVisibleAddSomePadding () {
      let self = this
      EventBus.onTourStartEvent(function () {
        self.isViewingTour = true
      })
      EventBus.onTourEndEvent(function () {
        self.isViewingTour = false
      })
    },
    /**
    * Event listener for unauthorized event
    */
    whenUserIsUnauthorized () {
      EventBus.onUnauthorizedEvent(this.logout)
    },
    /**
    * Event listener for logout event
    */
    whenUserClicksLogout () {
      EventBus.onLogoutEvent(this.logout)
    },
    /**
    * Unsubscribe event handlers, remove auth cookie and redirect user to login view.
    */
    logout (event) {
      let returnUrl = (!isNull(event) && objectHasKeys(event))
        ? event.returnUrl
        : null
      let localeFromParam = (!isNull(event) && objectHasKeys(event))
        ? event.locale
        : null
      this.removeAuthCookie()
      this.redirectToLoginView(returnUrl, localeFromParam)
      store.dispatch('user/locally/setPropToUser', {key: HIDE_CUBA_RECHARGE_BANNER, value: false})
      store.dispatch('reset')
    },
    /**
    * Removes the authentication cookie if exists.
    */
    removeAuthCookie () {
      this.$cookies.remove(AUTH_COOKIE_NAME, '/', process.env.COOKIE_DOMAIN)
    },
    /**
    * Redirect to login view with current user locale.
    */
    redirectToLoginView (returnUrl, localeFromParam) {
      let locale = localeFromParam || Translation.getUserSupportedLang()
      const route = {
        name: 'Signin',
        params: {
          locale: locale
        }
      }
      if (!isNull(returnUrl)) {
        route.query = {
          returnurl: returnUrl
        }
      }
      this.$router.push(route)
      // let logoutUrl = process.env.MY_URL + resource
      // window.location.href = logoutUrl
    },
    /**
     * Triggers log out tag from GTM on mParticle
     */
    /**
    * When user makes a purchase or redeems a voucher we need to update
    * user balance and products in our store to reflect the changes in ui.
    */
    whenUserBalanceChanges () {
      EventBus.onBalanceChangedEvent(function () {
        store.dispatch('user/fetchUser')
        store.dispatch('user/products/fetchUserProducts')
      })
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'app/isLoading'
    }),
    gtm () {
      let scriptBuilder = 'dataLayer = [];\n'
      scriptBuilder += '(function (w, d, s, l, i) {\n'
      scriptBuilder += '\tw[l] = w[l] || []; w[l].push({\n'
      scriptBuilder += '\t\t\t"gtm.start":\n'
      scriptBuilder += '\t\tnew Date().getTime(), event: "gtm.js"\n'
      scriptBuilder += '\t}); var f = d.getElementsByTagName(s)[0],\n'
      scriptBuilder += '\tj = d.createElement(s), dl = l != "dataLayer" ? "&l=" + l : ""; j.async = true; j.src =\n'
      scriptBuilder += '\t"//www.googletagmanager.com/gtm.js?id=" + i + dl + "' + process.env.GOOGLE_TAGMANAGER_QS + '"; f.parentNode.insertBefore(j, f);\n'
      scriptBuilder += '})(window, document, "script", "dataLayer", "' + process.env.GOOGLE_TAGMANAGER_KEY + '");'
      return scriptBuilder
    },
    gtmNoScript () {
      let scriptBuilder = '<iframe src="//www.googletagmanager.com/ns.html?id=' + process.env.GOOGLE_TAGMANAGER_KEY + process.env.GOOGLE_TAGMANAGER_QS + '"'
      scriptBuilder += ' height="0" width="0" style="display:none;visibility:hidden"></iframe>'
      return scriptBuilder
    }
  },
  data () {
    return {
      isViewingTour: false
    }
  },
  metaInfo () {
    return {
      titleTemplate: (titleChunk) => {
        return titleChunk ? `${titleChunk} | Rebtel` : this.$t('greetings.welcome')
      },
      // title: this.$t('label.loading'),
      meta: [
        { 'charset': 'utf-8' },
        { 'name': 'viewport', 'content': 'width=device-width,initial-scale=1.0' },
        { 'http-equiv': 'X-UA-Compatible', 'content': 'IE=edge' },
        { 'name': 'robots', 'content': 'noindex, nofollow' }
      ],
      script: [
        { innerHTML: this.gtm },
        { src: process.env.ADYEN_CSE_URL, defer: true },
        { src: 'https://cdn.seondf.com/js/v5/agent.js', defer: true },
        { src: 'https://cdn.optimizely.com/js/28909170182.js' }
        // { src: 'https://www.google.com/recaptcha/api.js', 'async': true, 'defer': true }
      ],
      noscript: [
        { innerHTML: this.gtmNoScript }
      ]
    }
  }
}
</script>

<style>
@import "tailwindcss/base";
@import "tailwindcss/components";
@import "tailwindcss/utilities";
</style>

<style lang="scss">
@import './assets/styles/index.scss';
// @import './assets/styles/tailwind.css';
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  &.tour-padding {
    padding-bottom: 18.75rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue